/**
 * AI-driven widget grid — the killer feature from FB-JKC6BNB9.
 *
 * Pulls widget specs from /api/v1/workspaces/:id/widgets (which reads
 * workspaces.ai_widgets JSONB column). Each spec is data:
 *   { widget_id, title, tool, params, layout: {x,y,w,h} }
 *
 * The grid renders each spec via the right widget component (looked up
 * by widget_id in the WIDGET_REGISTRY). New widgets ship as ~50 lines
 * of code each + a registry entry — adding a widget is fast.
 *
 * Drag/resize/persist via react-grid-layout. Layout changes POST back to
 * the server so they survive across sessions.
 */
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import GridLayout, { type Layout, type LayoutItem } from "react-grid-layout";
import "react-grid-layout/css/styles.css";
import "react-resizable/css/styles.css";
import { WIDGET_REGISTRY, WidgetSpec } from "../widgets/registry";
import { useEffect, useRef, useState } from "react";
import { GripVertical, LayoutGrid } from "lucide-react";

export function WidgetGrid({ workspaceId }: { workspaceId: string }) {
  const qc = useQueryClient();
  const [containerWidth, setContainerWidth] = useState(960);
  const [layoutRevision, setLayoutRevision] = useState(0);
  const containerRef = useRef<HTMLDivElement>(null);

  const { data: widgets, isLoading, error, refetch } = useQuery({
    queryKey: ["widgets", workspaceId],
    queryFn: async () => {
      const res = await fetch(
        `/api/v1/workspaces/${workspaceId}/widgets`,
        { credentials: "include" }
      );
      if (!res.ok) throw new Error("Failed to load widgets");
      const json = await res.json();
      return (json.widgets ?? []) as WidgetSpec[];
    },
  });

  const saveLayout = useMutation({
    mutationFn: async (layouts: LayoutItem[]) => {
      const csrf = document.cookie.match(/csrf_token=([^;]+)/)?.[1];
      const res = await fetch(
        `/api/v1/workspaces/${workspaceId}/widgets/layout`,
        {
          method: "POST",
          credentials: "include",
          headers: {
            "content-type": "application/json",
            ...(csrf ? { "x-csrf-token": csrf } : {}),
          },
          body: JSON.stringify({ layouts }),
        }
      );
      if (!res.ok) throw new Error("Failed to save layout");
      return res.json();
    },
    onSuccess: () => qc.invalidateQueries({ queryKey: ["widgets", workspaceId] }),
    onError: () => {
      // The grid keeps gesture state internally. Remount it with the last
      // saved specs, then fetch the authoritative layout after a failed save.
      setLayoutRevision((revision) => revision + 1);
      void qc.invalidateQueries({ queryKey: ["widgets", workspaceId] });
    },
  });

  // Track container width for responsive grid
  useEffect(() => {
    if (!containerRef.current) return;
    const ro = new ResizeObserver((entries) => {
      for (const e of entries) {
        setContainerWidth(e.contentRect.width);
      }
    });
    ro.observe(containerRef.current);
    return () => ro.disconnect();
  }, []);

  let content;
  if (isLoading) {
    content = (
      <div role="status" className="grid gap-3 grid-cols-1 md:grid-cols-2 xl:grid-cols-3">
        <span className="sr-only">Loading widgets…</span>
        {[0, 1, 2].map((i) => (
          <div key={i} className="cmcp-card space-y-2 p-4" aria-hidden="true">
            <span className="cmcp-skeleton h-3 w-1/3" />
            <span className="cmcp-skeleton h-16 w-full" />
          </div>
        ))}
      </div>
    );
  } else if (error && !widgets) {
    content = null;
  } else if (!widgets || widgets.length === 0) {
    content = (
      <div className="cmcp-empty">
        <div className="cmcp-empty-icon" aria-hidden="true"><LayoutGrid size={18} /></div>
        <p className="cmcp-empty-title">No widgets yet</p>
        <div className="cmcp-empty-body">
          Ask your AI client: <code className="bg-stone-100 px-1.5 py-0.5 rounded font-mono text-xs text-ink">"deploy a treasury unreconciled-disbursements widget"</code>
        </div>
      </div>
    );
  } else {
    const layout: Layout = widgets.map((w) => ({
      i: w.widget_id + ":" + (w.instance_id ?? "0"),
      x: w.layout?.x ?? 0,
      y: w.layout?.y ?? 0,
      w: w.layout?.w ?? 4,
      h: w.layout?.h ?? 3,
      minW: 2,
      minH: 2,
    }));

    content = (
      <GridLayout
        key={`${workspaceId}:${layoutRevision}`}
        className="layout"
        layout={layout}
        gridConfig={{ cols: 12, rowHeight: 70 }}
        dragConfig={{ handle: ".widget-drag-handle", enabled: !saveLayout.isPending }}
        resizeConfig={{ enabled: !saveLayout.isPending }}
        width={containerWidth}
        onDragStop={(newLayout) => {
          // Initialization, server refresh and responsive compaction also fire
          // onLayoutChange. Only a completed user gesture may persist layout.
          saveLayout.mutate(Array.from(newLayout));
        }}
        onResizeStop={(newLayout) => {
          saveLayout.mutate(Array.from(newLayout));
        }}
      >
        {widgets.map((w) => {
          const key = w.widget_id + ":" + (w.instance_id ?? "0");
          const Comp = WIDGET_REGISTRY[w.widget_id];
          return (
            <div key={key} className="cmcp-card overflow-hidden flex flex-col">
              <div className="widget-drag-handle border-b border-stone-100 px-3 py-2 flex items-center gap-2 cursor-move" title="Drag to rearrange">
                <div className="text-xs text-ink font-semibold flex-1 truncate">
                  {w.title || w.widget_id}
                </div>
                <GripVertical size={14} aria-hidden="true" className="shrink-0 text-stone-400" />
              </div>
              <div className="flex-1 overflow-auto p-3">
                {Comp ? (
                  <Comp spec={w} workspaceId={workspaceId} />
                ) : (
                  <div className="text-xs text-stone-500">
                    Unknown widget type: <code>{w.widget_id}</code>
                  </div>
                )}
              </div>
            </div>
          );
        })}
      </GridLayout>
    );
  }

  // Keep the measured element mounted through loading, errors and empty
  // results, so its ResizeObserver also catches the first async grid render.
  return (
    <div ref={containerRef} className="-mx-3">
      {error && (
        <div role="alert" className="mb-3 rounded border border-red-200 bg-red-50 p-3 text-sm text-red-800">
          Could not load widgets. {widgets ? "Showing the last loaded widgets. " : ""}
          <button type="button" className="font-medium underline" onClick={() => void refetch()}>Retry loading widgets</button>
        </div>
      )}
      {saveLayout.isError && (
        <div role="alert" className="mb-3 rounded border border-red-200 bg-red-50 p-3 text-sm text-red-800">
          Could not save the layout. Reloading the saved layout.
          {" "}<button type="button" className="font-medium underline" onClick={() => void refetch()}>Reload widgets</button>
        </div>
      )}
      {saveLayout.isPending && <p role="status" className="mb-2 text-xs text-stone-600">Saving layout…</p>}
      {content}
    </div>
  );
}
